import { useState, useCallback, useRef, useEffect } from "react";

const SERVER_UNAVAILABLE_MESSAGE = "The server is unavailable. Please try again in a moment.";

export const useHttpClient = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState();

  const activeHttpRequest = useRef([]);

  const sendRequest = useCallback(
    async (url, method = "GET", body = null, headers = {}) => {
      setIsLoading(true);

      const httpAbortCtrl = new AbortController();
      activeHttpRequest.current.push(httpAbortCtrl);

      try {
        const response = await fetch(url, {
          method,
          body,
          headers,
          signal: httpAbortCtrl.signal,
        });

        // Log the response details BEFORE parsing
        console.log('Response status:', response.status);
        console.log('Response headers:', response.headers);
        console.log('Content-Type:', response.headers.get('content-type'));

        // Check if response is actually JSON
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          const textResponse = await response.text();
          console.error('Expected JSON but got:', textResponse);
          throw new Error(SERVER_UNAVAILABLE_MESSAGE);
        }

        const responseData = await response.json();

        // clear abort controllers that below to the request that was completed
        activeHttpRequest.current = activeHttpRequest.current.filter(
          (reqCtrl) => reqCtrl !== httpAbortCtrl
        );

        if (!response.ok) {
          const errorMessage = responseData.message || 'Request failed';
          setError(errorMessage);
          setIsLoading(false);
          throw new Error(errorMessage);
        }

        setIsLoading(false);
        return responseData;

      } catch (err) {
        // Don't set error state if request was aborted (component unmounting)
        if (err.name === 'AbortError') {
          console.log('Request was cancelled');
          return; // Don't throw, just exit
        }
        // fetch() rejects with a TypeError ("Failed to fetch") when the server is unreachable
        setError(err instanceof TypeError ? SERVER_UNAVAILABLE_MESSAGE : err.message);
        setIsLoading(false);
        throw err;
      }
    },
    []
  );

  const clearError = () => {
    setError(null);
  };

  // what is this for
  useEffect(() => {
    return () => {
      activeHttpRequest.current.forEach((abortCtrl) => abortCtrl.abort());
    };
  }, []);

  return { isLoading, error, sendRequest, clearError };
};
